import { differenceInSeconds } from 'date-fns';
import type { Interval } from './types';
import { TIME_BAR_ITEM_WIDTH } from 'constants/widths';
import { TIMELINE_SUMMARY_WIDTH } from './TimelineRoute/Summary/Summary';
import { ZoomLevel } from './TimelineBar/TimelineBar.enums';

/** Timeline utils */
export const getAppointmentWidth = (
  routeDurationInSeconds,
  appointmentDurationInSeconds
) => {
  // config values
  const workDuration = 60 * 60 * 9; // 9 hours
  const percentile = workDuration / 100;

  // calculate the scale
  const routeComparedToWorkDuration = routeDurationInSeconds / workDuration;
  const remainder = routeComparedToWorkDuration - 1;
  const scale = remainder > 0 ? 1 - remainder : 1;

  // return the width
  return (appointmentDurationInSeconds / percentile) * scale;
};

export const getPercentageOfTotalDuration = (durationInSeconds, percentile) =>
  durationInSeconds / percentile;

export const getSecondsPerPixel = (
  timelineWindowSize: number,
  zoomLevel: ZoomLevel
) => {
  return (3600 / timelineWindowSize) * getIntervalLengthInHours(zoomLevel);
};

export const getItemWidthInPixels = (
  timelineWindowSize: number,
  durationInSeconds: number,
  zoomLevel: ZoomLevel
) => {
  const secondsPerPixel = getSecondsPerPixel(timelineWindowSize, zoomLevel);
  return durationInSeconds / secondsPerPixel;
};

export const getStartingPoint = (
  refDate: Date | string,
  interval: Interval,
  zoomLevel: ZoomLevel,
  timelineWindowSize: number
) => {
  const startDiff = differenceInSeconds(new Date(refDate), interval.start);
  return getItemWidthInPixels(timelineWindowSize, startDiff, zoomLevel);
};

export const alignScrollPosition = (
  scrollContainer: HTMLDivElement | null | undefined,
  zoomLevel: ZoomLevel,
  previousZoomLevel: ZoomLevel
) => {
  if (!scrollContainer) return;

  const zoomFactor = zoomLevel / previousZoomLevel;
  const x = Math.min(
    scrollContainer.scrollLeft * zoomFactor,
    scrollContainer.scrollWidth - scrollContainer.clientWidth
  );

  scrollContainer.scrollTo({ left: x });
};

const getZoomLevelArray = (): ZoomLevel[] => {
  return Object.keys(ZoomLevel)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ZoomLevel[key as keyof typeof ZoomLevel]);
};

export const getPreviousZoomLevel = (current: ZoomLevel): ZoomLevel => {
  const levels = getZoomLevelArray();
  const index = levels.findIndex((level) => level === current);
  return index > 0 ? levels[index - 1] : levels[0];
};

export const getNextZoomLevel = (current: ZoomLevel): ZoomLevel => {
  const levels = getZoomLevelArray();
  const index = levels.findIndex((level) => level === current);
  return index < levels.length - 1
    ? levels[index + 1]
    : levels[levels.length - 1];
};

export const hasPreviousZoomLevel = (current: ZoomLevel): boolean => {
  const levels = getZoomLevelArray();
  const index = levels.findIndex((level) => level === current);
  return index > 0;
};

export const hasNextZoomLevel = (current: ZoomLevel): boolean => {
  const levels = getZoomLevelArray();
  const index = levels.findIndex((level) => level === current);
  return index < levels.length - 1;
};

export const getIntervalLengthInHours = (zoomLevel: ZoomLevel) => {
  switch (zoomLevel) {
    case ZoomLevel.XXXXS:
    case ZoomLevel.XXXS:
      return 12;
    default:
      return 1 / zoomLevel;
  }
};

export const getTimeBarItemWidth = (
  interval: Interval,
  clientWidth: number
) => {
  if (interval.timeWindow) {
    const timeBarWidth = interval.timeWindow.length * TIME_BAR_ITEM_WIDTH;
    if (timeBarWidth < clientWidth - TIMELINE_SUMMARY_WIDTH) {
      //30 is for margin and scrollbar width
      return (
        (clientWidth - TIMELINE_SUMMARY_WIDTH - 30) / interval.timeWindow.length
      );
    }
  }
  return TIME_BAR_ITEM_WIDTH;
};
